import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Button,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import {
  Science,
  AccessTime,
  Assessment,
  Refresh,
  DateRange,
  CalendarToday,
  BarChart,
  Timeline,
  TrendingUp,
  Warning,
} from '@mui/icons-material';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { samplesService } from '../../services/samplesService';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend
);

interface SampleProcessingMetrics {
  laboratoryMetrics: {
    SAA: { samplesProcessed: number; avgProcessingTime: number };
    ICP: { samplesProcessed: number; avgProcessingTime: number };
    AU: { samplesProcessed: number; avgProcessingTime: number };
    VOLUMETRIE: { samplesProcessed: number; avgProcessingTime: number };
  };
}

const WorkflowAnalytics: React.FC = () => {
  const [metrics, setMetrics] = useState<SampleProcessingMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({
    startDate: '2025-08-01', // Use current month where samples exist
    endDate: '2025-08-31',   // Use current month where samples exist
    preset: '30d' as '7d' | '30d' | '90d' | 'custom'
  });
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const fetchSampleProcessingData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('📊 Fetching sample processing metrics for date range:', dateRange);

      const data = await samplesService.getSampleProcessingMetrics(
        dateRange.startDate,
        dateRange.endDate
      );

      console.log('📊 Received sample processing data:', data);
      setMetrics(data);
      setLastUpdate(new Date());
    } catch (err: any) {
      console.error('❌ Error fetching sample processing data:', err);
      setError(err.message || 'Erreur lors du chargement des métriques de traitement');
    } finally {
      setLoading(false);
    }
  };

  const handleDatePresetChange = (preset: '7d' | '30d' | '90d' | 'custom') => {
    let startDate: string;
    const endDate = new Date().toISOString().split('T')[0];

    switch (preset) {
      case '7d':
        startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case '30d':
        startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case '90d':
        startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      default:
        return; // Don't change dates for custom
    }

    setDateRange({ startDate, endDate, preset });
  };

  useEffect(() => {
    fetchSampleProcessingData();
  }, [dateRange.startDate, dateRange.endDate]);

  // Prepare chart data for laboratory metrics
  const laboratoryChartData = {
    labels: ['SAA', 'ICP', 'AU', 'VOLUMETRIE'],
    datasets: [
      {
        label: 'Échantillons Traités',
        data: metrics ? [
          metrics.laboratoryMetrics.SAA.samplesProcessed,
          metrics.laboratoryMetrics.ICP.samplesProcessed,
          metrics.laboratoryMetrics.AU.samplesProcessed,
          metrics.laboratoryMetrics.VOLUMETRIE.samplesProcessed,
        ] : [],
        backgroundColor: [
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 99, 132, 0.8)',
          'rgba(255, 206, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
        ],
        borderWidth: 2,
      },
    ],
  };

  // Prepare chart data for average processing time per lab
  const labTimeChartData = {
    labels: ['SAA', 'ICP', 'AU', 'VOLUMETRIE'],
    datasets: [
      {
        label: 'Temps Moyen (heures)',
        data: metrics ? [
          metrics.laboratoryMetrics.SAA.avgProcessingTime,
          metrics.laboratoryMetrics.ICP.avgProcessingTime,
          metrics.laboratoryMetrics.AU.avgProcessingTime,
          metrics.laboratoryMetrics.VOLUMETRIE.avgProcessingTime,
        ] : [],
        backgroundColor: [
          'rgba(75, 192, 192, 0.8)',
          'rgba(255, 159, 64, 0.8)',
          'rgba(153, 102, 255, 0.8)',
          'rgba(255, 99, 132, 0.8)',
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 99, 132, 1)',
        ],
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  if (loading && !metrics) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Date Range Selector */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DateRange color="primary" />
              Sélection de Période
            </Typography>
          </Grid>
          <Grid item>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Période</InputLabel>
              <Select
                value={dateRange.preset}
                label="Période"
                onChange={(e) => handleDatePresetChange(e.target.value as any)}
              >
                <MenuItem value="7d">7 derniers jours</MenuItem>
                <MenuItem value="30d">30 derniers jours</MenuItem>
                <MenuItem value="90d">90 derniers jours</MenuItem>
                <MenuItem value="custom">Personnalisé</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <TextField
              label="Date début"
              type="date"
              size="small"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value, preset: 'custom' }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item>
            <TextField
              label="Date fin"
              type="date"
              size="small"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value, preset: 'custom' }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item>
            <IconButton onClick={fetchSampleProcessingData} disabled={loading}>
              <Refresh />
            </IconButton>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {metrics && Object.entries(metrics.laboratoryMetrics).map(([lab, data], index) => (
          <Grid item xs={12} sm={6} md={3} key={lab}>
            <Card sx={{ 
              bgcolor: ['primary.light', 'secondary.light', 'warning.light', 'success.light'][index], 
              color: 'white' 
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Science sx={{ mr: 1 }} />
                  <Typography variant="h6">{lab}</Typography>
                </Box>
                <Typography variant="h3">{data.samplesProcessed}</Typography>
                <Typography variant="caption">
                  échantillons traités
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Temps moyen: {data.avgProcessingTime.toFixed(1)}h
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Laboratory Processing Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BarChart color="primary" />
                Échantillons Traités par Laboratoire
              </Typography>
              <Box sx={{ height: 300 }}>
                <Bar data={laboratoryChartData} options={chartOptions} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Nombre d'échantillons traités du {new Date(dateRange.startDate).toLocaleDateString('fr-FR')} au {new Date(dateRange.endDate).toLocaleDateString('fr-FR')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Average Time per Lab Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Timeline color="primary" />
                Temps Moyen de Traitement par Laboratoire
              </Typography>
              <Box sx={{ height: 300 }}>
                <Bar data={labTimeChartData} options={chartOptions} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Temps moyen de traitement de la réception au statut PRÊT (en heures)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Summary Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Timeline color="primary" />
                Temps Moyen par Laboratoire (Réception → Prêt)
              </Typography>
              <Grid container spacing={3}>
                {metrics && Object.entries(metrics.laboratoryMetrics).map(([lab, data]) => (
                  <Grid item xs={12} sm={6} md={3} key={lab}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="primary" gutterBottom>
                          {data.avgProcessingTime}h
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                          {lab}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {data.samplesProcessed} échantillons
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Chip 
                            label={data.avgProcessingTime < 24 ? 'Excellent' : data.avgProcessingTime < 48 ? 'Bon' : 'À améliorer'}
                            color={data.avgProcessingTime < 24 ? 'success' : data.avgProcessingTime < 48 ? 'warning' : 'error'}
                            size="small"
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WorkflowAnalytics;
